import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Order, useOrderStore } from "@/store/orderStore";
import { useCart } from "@/hooks/useCart";
import { CartIcon } from "./CartIcon";
import { CartContent } from "./CartContent";
import { OrderSuccessDialog } from "./OrderSuccessDialog";
import { CartConfirmationDialog } from "./CartConfirmationDialog";
import { getActiveOrdersForTable } from "@/services/tables/tableService";
import { useOrderSubmission } from "@/hooks/useOrderSubmission";
import { useCartDialog } from "./hooks/useCartDialog";
import { useTranslation } from "react-i18next";
import { MenuItem } from "@/types/menu";
import { useToast } from "@/hooks/use-toast";
import { CashPaymentSuggestionModal } from "./CashPaymentSuggestionModal";
import { useSearchParams } from "react-router-dom";

interface PublicMenuCartProps {
  tableId: string;
  tableName: string;
  tableNumber: number;
  storeId: string;
  onOrderPlaced: (order: Order) => void;
}

export function PublicMenuCart({
  tableId,
  tableName,
  tableNumber,
  storeId,
  onOrderPlaced,
}: PublicMenuCartProps) {
  const [activeOrder, setActiveOrder] = useState<Order | null>(null);
  const [showCashSuggestionModal, setShowCashSuggestionModal] = useState(false);
  const { setSelectedStoreId, initGlobalSubscription } = useOrderStore();
  const { t } = useTranslation();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();

  const {
    cartItems,
    totalAmount,
    totalItems,
    itemCount,
    specialInstructions,
    setSpecialInstructions,
    selectedTip,
    setSelectedTip,
    updateQuantity,
    removeItem,
    clearCart,
    calculateTotal,
    calculateSubtotal,
    addItemToCart,
    restoreCartFromPendingOrder,
  } = useCart(tableId);

  const {
    isCartOpen,
    setIsCartOpen,
    isConfirmDialogOpen,
    setIsConfirmDialogOpen,
    handleCartIconClick,
    handleConfirmOpenCart,
  } = useCartDialog({ activeOrder });

  const {
    isSubmitting,
    isSuccessDialogOpen,
    setIsSuccessDialogOpen,
    handleSubmitOrder,
  } = useOrderSubmission({
    storeId,
    tableId,
    tableName,
    tableNumber,
    specialInstructions,
    cartItems,
    selectedTip,
    onOrderPlaced: (order) => {
      // Set this order as the active order
      setActiveOrder(order);

      // Notify parent component
      if (onOrderPlaced) {
        onOrderPlaced(order);
      }
    },
    clearCart,
    calculateTotal,
    calculateSubtotal,
  });

  // Set the store ID in the global store as soon as this component mounts
  // This ensures real-time subscriptions are properly setup
  useEffect(() => {
    if (storeId) {
      setSelectedStoreId(storeId);

      // Initialize global subscription
      initGlobalSubscription();

      // Cleanup function
      return () => {
        useOrderStore.getState().unsubscribeFromOrdersRealtime();
      };
    }
  }, [storeId, setSelectedStoreId, initGlobalSubscription]);

  // Check for active orders when component mounts
  useEffect(() => {
    const checkActiveOrders = async () => {
      const activeOrders = await getActiveOrdersForTable(tableId);
      if (activeOrders && activeOrders.length > 0) {
        setActiveOrder(activeOrders[0]);
      }
    };

    if (tableId) {
      checkActiveOrders();
    }
  }, [tableId]);

  // Handle cart restoration and cash payment suggestion from URL parameters
  useEffect(() => {
    const restoreCart = searchParams.get("restoreCart");
    const showCashSuggestion = searchParams.get("showCashSuggestion");

    if (restoreCart === "true" && showCashSuggestion === "true") {
      // Cart should already be restored by the useCart hook
      // Just show the cash payment suggestion modal
      setShowCashSuggestionModal(true);
      
      // Clean up URL parameters
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete("restoreCart");
      newUrl.searchParams.delete("showCashSuggestion");
      window.history.replaceState({}, "", newUrl.toString());
    }
  }, [searchParams]);

  // Listen for menu item additions globally and handle them
  useEffect(() => {
    const handleMenuItemAdd = (event: CustomEvent<MenuItem>) => {
      const item = event.detail;

      if (tableId && storeId) {
        // Convert price to number if it's a string
        const price =
          typeof item.price === "string" ? parseFloat(item.price) : item.price;

        // Add item to cart using the hook with correct property names
        addItemToCart({
          id: item.id, // This will be used as menuItemId internally
          name: item.name,
          quantity: 1,
          price: price,
          imageUrl: item.image_url,
        });

        // Show toast notification
        toast({
          title: t("addedToCart"),
          description: `${item.name} ${t("hasBeenAddedToYourCart")}`,
          duration: 2000,
        });
      }
    };

    window.addEventListener("add-to-cart", handleMenuItemAdd as EventListener);

    return () => {
      window.removeEventListener(
        "add-to-cart",
        handleMenuItemAdd as EventListener
      );
    };
  }, [tableId, storeId, addItemToCart, toast, t]);

  const handleProceedWithCash = async () => {
    try {
      await handleSubmitOrder("Cash");
      setShowCashSuggestionModal(false);
    } catch (error) {
      console.error("Error proceeding with cash payment:", error);
      toast({
        title: t("error"),
        description: t("cashPaymentError", "Failed to process cash payment. Please try again."),
        variant: "destructive",
      });
    }
  };

  const handleCloseCart = () => {
    clearCart();
    setIsCartOpen(false);
  };

  return (
    <>
      <CartIcon itemCount={itemCount} onClick={handleCartIconClick} />

      <Dialog open={isCartOpen} onOpenChange={setIsCartOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("yourOrder")}</DialogTitle>
            <DialogDescription>
              {t("table")} {tableNumber}: {tableName}
            </DialogDescription>
          </DialogHeader>

          <CartContent
            cartItems={cartItems}
            specialInstructions={specialInstructions}
            setSpecialInstructions={setSpecialInstructions}
            selectedTip={selectedTip}
            setSelectedTip={setSelectedTip}
            updateQuantity={updateQuantity}
            removeItem={removeItem}
            clearCart={clearCart}
            handleSubmitOrder={handleSubmitOrder}
            isSubmitting={isSubmitting}
            setIsCartOpen={setIsCartOpen}
            storeId={storeId}
            tableId={tableId}
            calculateSubtotal={calculateSubtotal}
          />
        </DialogContent>
      </Dialog>

      <CartConfirmationDialog
        open={isConfirmDialogOpen}
        onOpenChange={setIsConfirmDialogOpen}
        onConfirm={handleConfirmOpenCart}
        orderStatus={activeOrder?.status || "pending"}
      />

      <CashPaymentSuggestionModal
        open={showCashSuggestionModal}
        onOpenChange={setShowCashSuggestionModal}
        onProceedWithCash={handleProceedWithCash}
        onCloseCart={handleCloseCart}
        totalAmount={calculateTotal()}
        paymentMethod="Digital"
      />
    </>
  );
}
